import type { AxiosError, AxiosInstance } from 'axios'
import * as Sentry from '@sentry/vue'
import globalAxios from 'axios'
import { isThereAnActiveOrgChange } from './managers/orgChangeManagement'
import { useUserStore } from './stores/user'
import { BRAND_NAME } from '~/constants/branding'
import { endpointTemplate } from '~/noResponseRetry'
import { isRequestAbort } from '~/requestAbort'
import Notification from '~/services/notificationService'

export function installAPIErrorHandlingInterceptor(
  axiosInstance?: AxiosInstance
) {
  if (!axiosInstance) {
    axiosInstance = globalAxios
  }
  axiosInstance.interceptors.response.use(
    (response) => response,
    handleAPIError
  )
}
/**
 * Extracts a user-friendly error message from an AxiosError.
 * Used by the global error handler and can be used for custom error handling.
 */
export function getErrorMessage(error: AxiosError<any>): string {
  const errData = error.response?.data as any
  if (error.response?.status === 422) {
    try {
      return errData.detail.map((detail: any) => detail.msg).join(', ')
    } catch (e) {
      Sentry.captureException(e)
      return 'An error occurred due to invalid input'
    }
  } else if (errData?.message) {
    return errData.message
  } else if (error.response) {
    return `${error.response.status} ${error.message} - ${error.response.statusText.slice(0, 40)}`
  } else if (
    error.message === 'Network Error' ||
    error.code === 'ERR_NETWORK'
  ) {
    return `Could not reach ${BRAND_NAME}. Try again.`
  } else {
    return error.message
  }
}

// Long enough to fold one burst. Short enough that a second click still shows.
const TRANSIENT_TOAST_WINDOW_MS = 1000
let lastTransientToast = { key: '', at: 0 }

/** Tests only. The quiet window is module state, so a later test would hide a real popup. */
export function resetTransientToastDedupe() {
  lastTransientToast = { key: '', at: 0 }
}

function transientToastKey(error: AxiosError<any>, message: string): string {
  // Several dropped calls share one sentence. One popup is enough.
  // A real server error stays tied to its own call, so a different one still shows.
  if (!error.response) {
    return message
  }
  const method = (error.config?.method || 'GET').toUpperCase()
  return `${method} ${endpointTemplate(error.config?.url)} ${message}`
}

function shouldShowTransientToast(key: string): boolean {
  const now = Date.now()
  if (
    lastTransientToast.key === key &&
    now - lastTransientToast.at < TRANSIENT_TOAST_WINDOW_MS
  ) {
    return false
  }
  lastTransientToast = { key, at: now }
  return true
}

export function handleAPIError(error: AxiosError<any>) {
  // If our API call said to skip error handling, do so
  if (error.config?.skipErrorHandling) {
    return Promise.reject(error)
  }

  // A call we stopped ourselves. A real drop has no aborted signal, so it still shows the popup.
  if (isRequestAbort(error)) {
    return Promise.reject(error)
  }
  updateSentryContexts(error)
  if (error.response?.status === 429) {
    // do nothing, don't need to see these errors
    return Promise.reject(error)
  }
  const msg = getErrorMessage(error)
  if (error.response?.status === 401) {
    if (!error.response.config.headers.Authorization) {
      console.warn(
        `Received 401 on a request that did not have the Authorization header set: ${error.response.config.url}`
      )
      Sentry.captureException(
        'Received 401 on a request that did not have the Authorization header set',
        {
          tags: {
            api_url: error.response.config.url,
          },
        }
      )
      return Promise.reject(error)
    }
    if (isThereAnActiveOrgChange()) {
      console.warn(
        `Received 401 on ${error.response.config.url} during org change, suppressing logout to prevent undesired logout`
      )
      return Promise.reject(error) // Reject but don't logout
    }

    const userStore = useUserStore()
    // eslint-disable-next-line no-console
    console.log('Logging user out after receiving 401')
    userStore.logout()
    return Promise.resolve()
  }
  const isTransient =
    !error.response ||
    (error.response.status >= 500 && error.response.status <= 504)
  if (isTransient && !shouldShowTransientToast(transientToastKey(error, msg))) {
    return Promise.reject(error)
  }
  Notification.error({
    title: 'Error',
    message: msg,
  })
  return Promise.reject(error)
}

/**
 * For a call made with `skipErrorHandling` that failed for good. Runs the
 * normal handling (toast, 401 logout) that the skip held back.
 * True only when that handling logged the user out.
 */
export async function handleSkippedAPIError(error: unknown): Promise<boolean> {
  if (!error || typeof error !== 'object') {
    return false
  }
  const err = error as AxiosError<any>
  if (err.config) {
    err.config = { ...err.config, skipErrorHandling: undefined }
  }
  try {
    await handleAPIError(err)
    return true
  } catch {
    return false
  }
}

function updateSentryContexts(error: AxiosError<any>) {
  let sentryTransactionName = 'Axios API Error'
  if (error.response && error.response.config.url) {
    try {
      const path = error.response.config.url
        .replace('https://api.draydog.com', '')
        .split('?')[0]
      const method = error.response.config.method!.toUpperCase()
      sentryTransactionName = `${method} ${path}`
      Sentry.setContext('request', {
        url: error.response.config.url,
        body: error.response.config.data,
      })
      if (error.response.data) {
        Sentry.setContext('response', {
          body: JSON.stringify(error.response.data),
        })
      }
    } catch (e) {
      Sentry.captureException(e)
    }
  }
  Sentry.setTag('transaction', sentryTransactionName)
}
